import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { Button } from './ui';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';
import './MemberMergeModal.css';
import { translateError } from '../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface IGroupDisplay {
  _id: string;
  name: string;
}

interface IUserDisplay {
  _id: string;
  name: string;
  email: string;
}

interface IOriginalRegistration {
  event?: {
    _id: string;
    name: string;
    startTime: string;
  };
  registeredAt?: string;
}

export interface IMemberForMerge {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  city?: string;
  dateOfBirth?: string;
  user?: IUserDisplay;
  groups?: IGroupDisplay[];
  /**
   * Count of activity enrollments. 0 means the member is "unattached" —
   * the Phase-1 replacement for the legacy `type === 'external'` check.
   */
  activityCount?: number;
  originalRegistration?: IOriginalRegistration;
  createdAt?: string;
}

interface MemberMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceMembers: IMemberForMerge[];
  onMergeComplete: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * MemberMergeModal - Modal for merging duplicate members
 *
 * Allows selecting a target member to merge others into.
 * Shows preview of affected records and confirmation before merge.
 */
const MemberMergeModal: React.FC<MemberMergeModalProps> = ({
  isOpen,
  onClose,
  sourceMembers,
  onMergeComplete,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [merging, setMerging] = useState(false);
  const [step, setStep] = useState<'select' | 'confirm'>('select');

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setSelectedTargetId(sourceMembers[0]?._id || '');
      setStep('select');
      setMerging(false);
    }
  }, [isOpen, sourceMembers]);

  // Members that will be merged into the target
  const membersToMerge = useMemo(() => {
    return sourceMembers.filter(m => m._id !== selectedTargetId);
  }, [sourceMembers, selectedTargetId]);

  // Selected target member
  const targetMember = useMemo(() => {
    return sourceMembers.find(m => m._id === selectedTargetId);
  }, [sourceMembers, selectedTargetId]);

  const handleMerge = async () => {
    if (!selectedTargetId || membersToMerge.length === 0) return;

    setMerging(true);
    try {
      // Merge each source member into the target
      for (const sourceMember of membersToMerge) {
        await api.members.merge(selectedTargetId, sourceMember._id);
      }

      addToast(
        t('members.mergeSuccess', {
          count: membersToMerge.length,
          target: targetMember?.name,
        }),
        'success'
      );
      onMergeComplete();
      onClose();
    } catch (err: any) {
      addToast(
        translateError(err, t, 'members.mergeError'),
        'error'
      );
    } finally {
      setMerging(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const renderMemberDetails = (member: IMemberForMerge) => (
    <div className="member-merge-modal__member-details">
      <div className="member-merge-modal__detail">
        <span className="member-merge-modal__label">{t('common.email')}:</span>
        <span>{member.email || '-'}</span>
      </div>
      <div className="member-merge-modal__detail">
        <span className="member-merge-modal__label">{t('common.phoneNumber')}:</span>
        <span>{member.phone || '-'}</span>
      </div>
      <div className="member-merge-modal__detail">
        <span className="member-merge-modal__label">{t('members.city')}:</span>
        <span>{member.city || '-'}</span>
      </div>
      <div className="member-merge-modal__detail">
        <span className="member-merge-modal__label">{t('members.dateOfBirth')}:</span>
        <span>{formatDate(member.dateOfBirth)}</span>
      </div>
      {member.groups && member.groups.length > 0 && (
        <div className="member-merge-modal__detail">
          <span className="member-merge-modal__label">{t('members.assignGroups')}:</span>
          <span>{member.groups.map(g => g.name).join(', ')}</span>
        </div>
      )}
      {member.originalRegistration?.event && (
        <div className="member-merge-modal__detail">
          <span className="member-merge-modal__label">{t('members.registeredFrom')}:</span>
          <span>{member.originalRegistration.event.name}</span>
        </div>
      )}
    </div>
  );

  const renderSelectStep = () => (
    <>
      <p className="member-merge-modal__description">
        {t('members.mergeSelectTarget')}
      </p>

      <div className="member-merge-modal__members-list">
        {sourceMembers.map(member => (
          <label
            key={member._id}
            className={`member-merge-modal__member-option ${
              selectedTargetId === member._id ? 'member-merge-modal__member-option--selected' : ''
            }`}
          >
            <input
              type="radio"
              name="targetMember"
              value={member._id}
              checked={selectedTargetId === member._id}
              onChange={() => setSelectedTargetId(member._id)}
            />
            <div className="member-merge-modal__member-info">
              <div className="member-merge-modal__member-name">
                {member.name}
                {(member.activityCount ?? 0) === 0 ? (
                  <span className="member-merge-modal__badge member-merge-modal__badge--external">
                    {t('members.unattached')}
                  </span>
                ) : (
                  <span className="member-merge-modal__badge member-merge-modal__badge--internal">
                    {t('members.attached')}
                  </span>
                )}
              </div>
              {renderMemberDetails(member)}
            </div>
          </label>
        ))}
      </div>

      <div className="member-merge-modal__actions">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => setStep('confirm')}
          disabled={!selectedTargetId}
        >
          {t('common.continue')}
        </Button>
      </div>
    </>
  );

  const renderConfirmStep = () => (
    <>
      <div className="member-merge-modal__confirm-content">
        <div className="member-merge-modal__warning">
          <span className="member-merge-modal__warning-icon">!</span>
          <p>{t('members.mergeWarning')}</p>
        </div>

        <div className="member-merge-modal__preview">
          <h4>{t('members.mergePreview')}</h4>

          <div className="member-merge-modal__preview-section">
            <span className="member-merge-modal__preview-label">
              {t('members.mergeTarget')}:
            </span>
            <span className="member-merge-modal__preview-value member-merge-modal__preview-value--target">
              {targetMember?.name}
            </span>
          </div>

          <div className="member-merge-modal__preview-section">
            <span className="member-merge-modal__preview-label">
              {t('members.mergeSource', { count: membersToMerge.length })}:
            </span>
            <ul className="member-merge-modal__preview-list">
              {membersToMerge.map(m => (
                <li key={m._id}>{m.name}</li>
              ))}
            </ul>
          </div>

          <div className="member-merge-modal__preview-section">
            <span className="member-merge-modal__preview-label">
              {t('members.mergeAffected')}:
            </span>
            <ul className="member-merge-modal__preview-list">
              <li>{t('members.mergeAffectedAttendance')}</li>
              <li>{t('members.mergeAffectedGroups')}</li>
              <li>{t('members.mergeAffectedContact')}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="member-merge-modal__actions">
        <Button variant="secondary" onClick={() => setStep('select')}>
          {t('common.back')}
        </Button>
        <Button
          variant="danger"
          onClick={handleMerge}
          loading={merging}
        >
          {t('members.confirmMerge')}
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('members.mergeTitle')}
      size="medium"
    >
      <div className="member-merge-modal">
        {step === 'select' ? renderSelectStep() : renderConfirmStep()}
      </div>
    </Modal>
  );
};

export default MemberMergeModal;
